/**
 * Task-pane-lifetime previews for workbook range snapshots.
 *
 * The store is deliberately in memory. It never writes image bytes to chat
 * history, browser storage, Langfuse, or the server. Object URLs are revoked
 * when a preview is evicted or when the task pane unloads.
 */

/** Maximum number of exact snapshot previews retained by one task pane. */
const MAX_PREVIEW_COUNT = 12;
/** Maximum aggregate decoded image bytes retained by one task pane. */
const MAX_PREVIEW_BYTES = 25 * 1024 * 1024;

/** Validated PNG and display metadata accepted by the in-memory store. */
export interface SnapshotPreviewInput {
  /** MIME type used when constructing the browser Blob. */
  mimeType: "image/png";
  /** Normalized PNG base64 without a data-URI prefix. */
  base64: string;
  /** Canonical worksheet name shown in the preview caption. */
  sheetName: string;
  /** Captured A1 rectangle shown in the preview caption. */
  address: string;
  /** Decoded PNG size used for memory-budget accounting. */
  byteLength: number;
  /** PNG width read from its IHDR chunk. */
  width: number;
  /** PNG height read from its IHDR chunk. */
  height: number;
}

/** Live task-pane preview resolved by an opaque timeline-safe identifier. */
export interface SnapshotPreview {
  /** Opaque identifier stored in display-only timeline metadata. */
  id: string;
  /** Revocable browser URL for the in-memory PNG Blob. */
  objectUrl: string;
  /** Canonical worksheet name captured by the tool. */
  sheetName: string;
  /** Captured A1 rectangle. */
  address: string;
  /** Decoded image size used for memory-budget accounting. */
  byteLength: number;
  /** Rendered PNG width in pixels. */
  width: number;
  /** Rendered PNG height in pixels. */
  height: number;
  /** Creation time in Unix milliseconds. */
  createdAt: number;
}

/** Ordered live previews; insertion order is the eviction order. */
const previews = new Map<string, SnapshotPreview>();
/** React external-store listeners interested in preview availability. */
const listeners = new Set<() => void>();
/** Aggregate decoded bytes currently retained by `previews`. */
let totalBytes = 0;
/** Monotonic suffix that disambiguates previews created in the same millisecond. */
let nextPreviewId = 1;

/**
 * Notify every subscribed view that preview availability changed.
 *
 * @returns Nothing.
 */
function notify(): void {
  for (const listener of listeners) listener();
}

/**
 * Revoke one object URL and remove its bytes from memory accounting.
 *
 * @param preview - Live preview being discarded.
 * @returns Nothing.
 */
function revoke(preview: SnapshotPreview): void {
  URL.revokeObjectURL(preview.objectUrl);
  totalBytes = Math.max(0, totalBytes - preview.byteLength);
}

/**
 * Evict the earliest inserted preview when a store budget is exceeded.
 *
 * @returns Nothing.
 */
function evictOldest(): void {
  const oldest = previews.entries().next().value as
    | [string, SnapshotPreview]
    | undefined;
  if (!oldest) return;
  previews.delete(oldest[0]);
  revoke(oldest[1]);
}

/**
 * Decode normalized base64 into a Blob-compatible standalone buffer.
 *
 * @param base64 - Normalized PNG base64.
 * @returns Array buffer containing the exact decoded image bytes.
 */
function decodeBase64(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const buffer = new ArrayBuffer(binary.length);
  const bytes = new Uint8Array(buffer);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return buffer;
}

/**
 * Retain the exact PNG bytes being attached to the next model turn.
 *
 * @param input - Validated snapshot metadata and normalized PNG base64.
 * @returns Opaque id safe to retain in the visible timeline.
 */
export function storeSnapshotPreview(input: SnapshotPreviewInput): string {
  const id = `snapshot-${Date.now().toString(36)}-${nextPreviewId++}`;
  const blob = new Blob([decodeBase64(input.base64)], { type: input.mimeType });
  const preview: SnapshotPreview = {
    id,
    objectUrl: URL.createObjectURL(blob),
    sheetName: input.sheetName,
    address: input.address,
    byteLength: input.byteLength,
    width: input.width,
    height: input.height,
    createdAt: Date.now(),
  };
  previews.set(id, preview);
  totalBytes += input.byteLength;
  while (previews.size > MAX_PREVIEW_COUNT || totalBytes > MAX_PREVIEW_BYTES) {
    evictOldest();
  }
  notify();
  return id;
}

/**
 * Read one live preview without exposing its source bytes.
 *
 * @param id - Opaque preview identifier from display-only timeline metadata.
 * @returns The live preview, or `undefined` after eviction or task-pane reload.
 */
export function getSnapshotPreview(id: string | undefined): SnapshotPreview | undefined {
  return id ? previews.get(id) : undefined;
}

/**
 * Subscribe a React view to in-memory preview availability changes.
 *
 * @param listener - Callback invoked after storage, eviction, or clearing.
 * @returns Unsubscribe callback for the supplied listener.
 */
export function subscribeSnapshotPreviews(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Revoke and forget every preview retained by this task pane.
 *
 * @returns Nothing.
 */
export function clearSnapshotPreviews(): void {
  for (const preview of previews.values()) revoke(preview);
  previews.clear();
  totalBytes = 0;
  notify();
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", clearSnapshotPreviews, { once: true });
}
